import React from 'react';
import { Box, Button, ButtonBase, Dialog, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Translate } from 'react-localize-redux';
import AutoQcScoreChip from './AutoQcScoreChip';
import AssessmentStatus from './AssessmentStatus';
import ImageAssessment, { ImageAssessmentValue } from './ImageAssessment';
import { IdentifiableCorrection, IdentifiableRow, Thumbnail } from './ImageReviewCard';
import ShotTypeLabel from './ShotTypeLabel';
import ClinicalImageTile from '../../../components/general/ClinicalImageTile';
import { ReviewImage, dedupeImagesByFile, isCurtained } from './imageMeta';
import { QcCategoriesState } from './qcCategories';

interface LesionReviewDialogProps {
    /** 1-based position of the lesion within the visit, for the heading. */
    lesionNumber: number;
    /** The lesion's images. May carry duplicate eCRF records; collapsed here as the gallery does. */
    images: ReviewImage[];
    /** The image to open on. `null` closes the dialog. */
    openAt: string | null;
    canViewIdentifiable: boolean;
    /** The visit is approved (LOCKED): navigate and read, write nothing. */
    readOnly?: boolean;
    categoriesFor: (image: ReviewImage) => QcCategoriesState;
    assessmentFor: (encryptedFileUuid: string) => ImageAssessmentValue;
    onAssessmentChange: (encryptedFileUuid: string, next: ImageAssessmentValue) => void;
    onRetry?: (encryptedFileUuid: string) => void;
    /** Corrections already applied to the identifiable flag, keyed by `encryptedFileUuid`. */
    corrections?: Record<string, IdentifiableCorrection>;
    /**
     * Open the existing correction modal for one image (SKIN-243). Absent means the action is
     * not on offer — withheld by the caller exactly as it is on the card.
     */
    onCorrectIdentifiable?: (encryptedFileUuid: string) => void;
    onClose: () => void;
}

/**
 * The decrypt-failure square in the stage. The photograph itself fills whatever height the
 * dialog leaves (SKIN-787); this only has to leave room for the error text and Retry beneath it
 * on a short laptop screen.
 */
const FAILURE_SIZE = 240;
const STRIP_WIDTH = 64;
const STRIP_HEIGHT = 48;

/**
 * SKIN-784 — one lesion's photographs, one at a time: the photograph large on the left, its
 * feedback categories on the right.
 *
 * The assessment is the same `ImageAssessment` the cards carried, with the same rules: ticking a
 * category is "needs feedback", Correct is "correct", and a photograph is reviewed only once
 * one of the two happens. Changes are written as they happen, through the same whole-value
 * upsert, so the dialog holds no draft of its own — what it shows is what the visit screen
 * holds. The footer only moves on, to the next photograph nobody has assessed, and closes
 * after the last; walking past a photograph says nothing about it.
 *
 * Every image stays assessable whatever its pixels are doing — behind the identifiable
 * curtain, still decrypting, or failed — as on the card, because the restricted tier marks
 * curtained images and approval waits on every image. The slot is the card's own `Thumbnail`
 * drawn large, so the curtain-before-failure rule lives in one place.
 */
export default function LesionReviewDialog({
    lesionNumber,
    images,
    openAt,
    canViewIdentifiable,
    readOnly = false,
    categoriesFor,
    assessmentFor,
    onAssessmentChange,
    onRetry,
    corrections,
    onCorrectIdentifiable,
    onClose,
}: LesionReviewDialogProps) {
    const unique = React.useMemo(() => dedupeImagesByFile(images), [images]);

    // Where the reviewer is, which curtains they lifted and how far they zoomed — for this
    // opening only. Reset in render when the caller opens it somewhere new, rather than in an
    // effect, so the first frame of a new opening never shows the previous one's image.
    const fresh = () => ({ openedAt: openAt, uuid: openAt, revealed: new Set<string>(), zoom: 1 });
    const [session, setSession] = React.useState(fresh);
    if (session.openedAt !== openAt) {
        setSession(fresh());
    }

    if (openAt === null || unique.length === 0) return null;

    const found = unique.findIndex((image) => image.encryptedFileUuid === session.uuid);
    const index = found === -1 ? 0 : found;
    const image = unique[index];
    const uuid = image.encryptedFileUuid;
    const value = assessmentFor(uuid);

    // Zoom belongs to one photograph: the next one is first seen whole, never as a crop.
    const goTo = (to: number) => setSession((prev) => ({ ...prev, uuid: unique[to].encryptedFileUuid, zoom: 1 }));
    const setZoom = (zoom: number) => setSession((prev) => ({ ...prev, zoom }));

    // The next image nobody has assessed, wrapping round, never the one on screen — whose own
    // write may not have reached `assessmentFor` yet.
    let nextUnassessed = -1;
    for (let step = 1; step < unique.length; step += 1) {
        const candidate = (index + step) % unique.length;
        if (assessmentFor(unique[candidate].encryptedFileUuid).outcome === null) {
            nextUnassessed = candidate;
            break;
        }
    }

    const advance = () => {
        if (nextUnassessed === -1) onClose();
        else goTo(nextUnassessed);
    };

    const curtained = isCurtained(image) && !session.revealed.has(uuid);
    const reveal = () => setSession((prev) => ({ ...prev, revealed: new Set(prev.revealed).add(uuid) }));
    // The same guards `Thumbnail` applies before it draws pixels; here only to decide whether
    // the toolbar offers a zoom there is something to apply to.
    const showable = image.bytes.state === 'ready' && !curtained;

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            aria-labelledby="lesion-review-title"
            PaperProps={{
                // MUI types PaperProps as HTML attributes, which do not list data-*.
                ...({ 'data-testid': 'lesion-review-dialog', 'data-image-uuid': uuid } as object),
                // SKIN-787 — on a wide screen the dialog is the viewport's height and never scrolls
                // as a whole: the toolbar, the panel header and the footer action stay on screen,
                // and only the category list scrolls. Stacked on a narrow screen, the whole dialog
                // scrolls instead.
                sx: { overflowY: { xs: 'auto', md: 'hidden' }, height: { md: 'calc(100% - 64px)' } },
            }}
        >
            <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, height: { md: '100%' }, minHeight: 0 }}>
                <Box sx={{ flex: '999 1 560px', minWidth: 0, height: { md: '100%' }, display: 'flex', flexDirection: 'column', bgcolor: 'grey.100' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 4, py: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                        <Translate>
                            {({ translate }) => (
                                <React.Fragment>
                                    <IconButton
                                        onClick={() => goTo(index - 1)}
                                        disabled={index === 0}
                                        aria-label={String(translate('hospital.quality-check.review.previous-photo'))}
                                        data-testid="review-prev"
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => goTo(index + 1)}
                                        disabled={index === unique.length - 1}
                                        aria-label={String(translate('hospital.quality-check.review.next-photo'))}
                                        data-testid="review-next"
                                    >
                                        <ChevronRightIcon />
                                    </IconButton>
                                </React.Fragment>
                            )}
                        </Translate>
                        <Typography variant="subtitle1" component="h2" id="lesion-review-title">
                            <Translate id="hospital.quality-check.review.lesion" />
                            {` ${lesionNumber} · `}
                            <span data-testid="review-position">{`${index + 1} / ${unique.length}`}</span>
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {showable && (
                            <Translate>
                                {({ translate }) => (
                                    <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={session.zoom}
                                        // MUI emits null when the pressed option is clicked again;
                                        // the level stays where it is.
                                        onChange={(_event, next: number | null) => { if (next !== null) setZoom(next); }}
                                        aria-label={String(translate('hospital.quality-check.review.zoom'))}
                                        data-testid="zoom-control"
                                    >
                                        {ZOOM_LEVELS.map((level) => (
                                            <ToggleButton
                                                key={level}
                                                value={level}
                                                data-testid={`zoom-option-${level === 1 ? 'fit' : level * 100}`}
                                                sx={{ minWidth: 64 }}
                                            >
                                                {level === 1
                                                    ? <Translate id="hospital.quality-check.review.zoom-fit" />
                                                    : `${level * 100}%`}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                )}
                            </Translate>
                        )}
                    </Box>

                    {/*
                      * SKIN-787 — the stage takes the height the dialog leaves (`1 1 0`, so the photo's
                      * natural size never decides it); the photograph sizes to the stage in CSS.
                      */}
                    <Box
                        sx={{ flex: { md: '1 1 0' }, minHeight: 0, height: { xs: '60vh', md: 'auto' }, p: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                    >
                        <Thumbnail
                            image={image}
                            curtained={curtained}
                            canViewIdentifiable={canViewIdentifiable}
                            onReveal={reveal}
                            onRetry={onRetry}
                            height={FAILURE_SIZE}
                            // Keyed by image so its scroll position does not carry over.
                            renderReady={(blobUrl) => (
                                <ZoomableImage key={uuid} blobUrl={blobUrl} zoom={session.zoom} />
                            )}
                        />
                    </Box>

                    <Box
                        component="nav"
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, px: 4, py: 3, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
                    >
                        {unique.map((item, i) => {
                            const outcome = assessmentFor(item.encryptedFileUuid).outcome;
                            const isCurrent = i === index;
                            const showPixels = item.bytes.state === 'ready' && !isCurtained(item);
                            return (
                                <ButtonBase
                                    key={item.encryptedFileUuid}
                                    onClick={() => goTo(i)}
                                    aria-current={isCurrent ? 'true' : undefined}
                                    data-testid="review-strip-item"
                                    data-outcome={outcome ?? 'unassessed'}
                                    sx={{
                                        position: 'relative',
                                        width: STRIP_WIDTH,
                                        height: STRIP_HEIGHT,
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        bgcolor: 'grey.300',
                                        border: 2,
                                        borderColor: outcome === 'correct' ? 'success.main' : outcome === 'needs' ? 'warning.main' : 'grey.400',
                                        outline: isCurrent ? '2px solid' : 'none',
                                        outlineColor: 'primary.main',
                                        outlineOffset: 2,
                                    }}
                                >
                                    {showPixels && item.bytes.state === 'ready' && (
                                        <ClinicalImageTile blobUrl={item.bytes.blobUrl} width={STRIP_WIDTH} height={STRIP_HEIGHT} />
                                    )}
                                    <Box
                                        component="span"
                                        sx={{ position: 'absolute', left: 2, top: 2, px: 0.5, borderRadius: 0.5, fontSize: 11, color: 'common.white', bgcolor: 'rgba(0,0,0,0.6)' }}
                                    >
                                        {i + 1}
                                    </Box>
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ flex: '1 1 380px', maxWidth: 440, minWidth: 0, height: { md: '100%' }, minHeight: 0, display: 'flex', flexDirection: 'column', borderLeft: 1, borderColor: 'divider' }}>
                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 2, px: 5, pt: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" component="p">
                                {`${index + 1}. `}
                                <ShotTypeLabel fieldName={image.fieldName} />
                            </Typography>
                            <AssessmentStatus categories={categoriesFor(image)} value={value} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                <AutoQcScoreChip score={image.qualityScore} />
                            </Box>
                            <IdentifiableRow
                                image={image}
                                correction={corrections?.[uuid]}
                                onCorrect={onCorrectIdentifiable}
                            />
                        </Box>
                        <Translate>
                            {({ translate }) => (
                                <IconButton
                                    onClick={onClose}
                                    aria-label={String(translate('general.close'))}
                                    data-testid="review-close"
                                >
                                    <CloseIcon />
                                </IconButton>
                            )}
                        </Translate>
                    </Box>

                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 5, pb: 4 }} data-testid="review-panel-body">
                        <ImageAssessment
                            categories={categoriesFor(image)}
                            value={value}
                            onChange={(next) => onAssessmentChange(uuid, next)}
                            readOnly={readOnly}
                        />
                    </Box>

                    {!readOnly && (
                        <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', px: 5, py: 3, borderTop: 1, borderColor: 'divider' }}>
                            <Button
                                variant="contained"
                                onClick={advance}
                                endIcon={<ChevronRightIcon />}
                                data-testid="review-advance"
                                data-then={nextUnassessed === -1 ? 'close' : 'next'}
                            >
                                <Translate
                                    id={nextUnassessed === -1
                                        ? 'general.close'
                                        : 'hospital.quality-check.review.next-to-review'}
                                />
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </Dialog>
    );
}

/** Fit, then the two levels the design offers: framing, then surface detail. */
const ZOOM_LEVELS = [1, 1.5, 2.5];

/**
 * The photograph at the zoom chosen in the dialog's toolbar — the only place zoom changes
 * (David, 2026-11-03: buttons only, no click or pinch on the photograph). Zoomed, it pans by
 * dragging or scrolling; a zoom change keeps the centre of the current view.
 */
function ZoomableImage({ blobUrl, zoom }: { blobUrl: string; zoom: number }) {
    const stageRef = React.useRef<HTMLDivElement>(null);
    // The centre of the view as fractions of the image, kept across zoom changes.
    const view = React.useRef({ x: 0.5, y: 0.5 });
    const drag = React.useRef<{ x: number; y: number; left: number; top: number } | null>(null);

    React.useLayoutEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        el.scrollLeft = view.current.x * el.scrollWidth - el.clientWidth / 2;
        el.scrollTop = view.current.y * el.scrollHeight - el.clientHeight / 2;
    }, [zoom]);

    const zoomed = zoom > 1;

    return (
        <Box sx={{ width: '100%', height: '100%' }} data-testid="zoom-stage" data-zoom={zoom}>
            <Box
                ref={stageRef}
                sx={{ width: '100%', height: '100%', overflow: zoomed ? 'auto' : 'hidden', cursor: zoomed ? 'grab' : 'default' }}
                onScroll={() => {
                    const el = stageRef.current;
                    if (!el || el.scrollWidth === 0) return;
                    view.current = {
                        x: (el.scrollLeft + el.clientWidth / 2) / el.scrollWidth,
                        y: (el.scrollTop + el.clientHeight / 2) / el.scrollHeight,
                    };
                }}
                onPointerDown={(event) => {
                    const el = stageRef.current;
                    if (!zoomed || !el) return;
                    drag.current = { x: event.clientX, y: event.clientY, left: el.scrollLeft, top: el.scrollTop };
                }}
                onPointerMove={(event) => {
                    const el = stageRef.current;
                    const d = drag.current;
                    if (!d || !el) return;
                    el.scrollLeft = d.left - (event.clientX - d.x);
                    el.scrollTop = d.top - (event.clientY - d.y);
                }}
                onPointerUp={() => { drag.current = null; }}
                onPointerLeave={() => { drag.current = null; }}
            >
                <Box
                    component="img"
                    src={blobUrl}
                    alt=""
                    draggable={false}
                    data-testid="zoom-image"
                    sx={{ display: 'block', width: `${zoom * 100}%`, height: `${zoom * 100}%`, objectFit: 'contain', userSelect: 'none' }}
                />
            </Box>
        </Box>
    );
}
